import { useState, useCallback } from 'react';
import { Loader2, Search, Play, AlertTriangle, CheckCircle2, XCircle, CalendarClock } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import { invoicingAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

export function RedatePanel() {
  const [previewing, setPreviewing] = useState(false);
  const [applying, setApplying] = useState(false);
  const [preview, setPreview] = useState(null);
  const [applyResult, setApplyResult] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const runPreview = useCallback(async () => {
    setPreviewing(true);
    setPreview(null);
    setApplyResult(null);
    try {
      const { data } = await invoicingAPI.redatePreview();
      setPreview(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setPreviewing(false);
    }
  }, []);

  const runApply = useCallback(async () => {
    setConfirmOpen(false);
    if (!preview) return;
    const ids = preview.to_redate
      .filter((r) => r.date_changed)
      .map((r) => r.invoice_id);
    if (ids.length === 0) {
      toast.error('No drafts with date changes to apply.');
      return;
    }
    setApplying(true);
    try {
      const { data } = await invoicingAPI.redateApply(ids);
      setApplyResult(data);
      setPreview(null);
      if (data.applied > 0) {
        toast.success(`Re-dated ${data.applied} draft${data.applied > 1 ? 's' : ''}.`);
      }
      if (data.failed > 0) {
        toast.error(`${data.failed} draft${data.failed > 1 ? 's' : ''} failed.`);
      }
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setApplying(false);
    }
  }, [preview]);

  const dateChanges = preview?.to_redate?.filter((r) => r.date_changed) || [];
  const alreadyCorrect = preview?.to_redate?.filter((r) => !r.date_changed) || [];
  const canApply = preview && dateChanges.length > 0 && !applying && !previewing;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <CalendarClock className="h-5 w-5" />
          Re-date Drafts to Check-in Date
        </CardTitle>
        <p className="text-xs text-muted-foreground">
          Preview and apply date corrections on existing draft invoices. Sets each
          draft&apos;s date and deadline to its booking check-in date. Finalized
          invoices are never touched. Across all FR entities.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Action buttons */}
        <div className="flex gap-2">
          <Button onClick={runPreview} disabled={previewing || applying} variant="outline">
            {previewing
              ? <Loader2 className="h-4 w-4 animate-spin mr-2" />
              : <Search className="h-4 w-4 mr-2" />}
            Preview
          </Button>
          <Button
            onClick={() => setConfirmOpen(true)}
            disabled={!canApply}
            variant="default"
          >
            {applying
              ? <Loader2 className="h-4 w-4 animate-spin mr-2" />
              : <Play className="h-4 w-4 mr-2" />}
            Apply
          </Button>
        </div>

        {/* ── Preview results ── */}
        {preview && (
          <div className="space-y-4">
            {/* Summary counts */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="rounded-lg border p-3">
                <p className="text-xs text-muted-foreground">Will re-date</p>
                <p className="text-xl font-bold tabular-nums">{preview.date_changes}</p>
              </div>
              <div className="rounded-lg border p-3">
                <p className="text-xs text-muted-foreground">Already correct</p>
                <p className="text-xl font-bold tabular-nums">{preview.already_correct}</p>
              </div>
              <div className="rounded-lg border p-3">
                <p className="text-xs text-muted-foreground">Cancelled (skip)</p>
                <p className="text-xl font-bold tabular-nums">{preview.cancelled_drafts_count}</p>
              </div>
              <div className="rounded-lg border p-3">
                <p className="text-xs text-muted-foreground">No check-in (skip)</p>
                <p className="text-xl font-bold tabular-nums">{preview.missing_checkin_count}</p>
              </div>
            </div>

            {/* Blocked-after-redate warning */}
            {preview.blocked_after_redate_count > 0 && (
              <div className="rounded-lg border border-amber-300 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/30 p-3">
                <p className="text-sm font-medium flex items-center gap-1.5">
                  <AlertTriangle className="h-4 w-4 text-amber-500" />
                  {preview.blocked_after_redate_count} draft{preview.blocked_after_redate_count > 1 ? 's' : ''} will
                  still be blocked after re-dating
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  These drafts have a check-in date earlier than an already-finalized
                  invoice in the same entity. They can be re-dated but cannot be finalized
                  until older-dated invoices are finalized first.
                </p>
              </div>
            )}

            {/* Errors */}
            {preview.errors?.length > 0 && (
              <div className="rounded-lg border border-red-300 bg-red-50 dark:border-red-800 dark:bg-red-950/30 p-3">
                <p className="text-sm font-medium flex items-center gap-1.5">
                  <XCircle className="h-4 w-4 text-red-500" />
                  Errors fetching invoices
                </p>
                {preview.errors.map((e, i) => (
                  <p key={i} className="text-xs text-muted-foreground mt-1">
                    {e.entity_id}: {e.error}
                  </p>
                ))}
              </div>
            )}

            {/* Drafts to re-date table */}
            {dateChanges.length > 0 && (
              <div>
                <h4 className="text-sm font-medium mb-2">
                  Drafts to re-date ({dateChanges.length})
                </h4>
                <div className="overflow-x-auto max-h-96 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0">
                      <tr className="border-b bg-muted/30">
                        <th className="text-left px-3 py-2 font-medium">Uplisting ID</th>
                        <th className="text-left px-3 py-2 font-medium">Customer</th>
                        <th className="text-left px-3 py-2 font-medium">Guest</th>
                        <th className="text-left px-3 py-2 font-medium">Current Date</th>
                        <th className="text-left px-3 py-2 font-medium">New Date</th>
                        <th className="text-right px-3 py-2 font-medium">Amount</th>
                        <th className="text-center px-3 py-2 font-medium">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dateChanges.map((r) => (
                        <tr
                          key={r.invoice_id}
                          className={`border-b ${r.blocked_by_finalized ? 'bg-amber-50 dark:bg-amber-950/20' : ''}`}
                        >
                          <td className="px-3 py-2 font-mono text-xs">{r.uplisting_id}</td>
                          <td className="px-3 py-2 text-xs">{r.customer_name}</td>
                          <td className="px-3 py-2 text-xs">{r.guest_name}</td>
                          <td className="px-3 py-2 font-mono text-xs text-red-600 dark:text-red-400">{r.current_date}</td>
                          <td className="px-3 py-2 font-mono text-xs text-green-600 dark:text-green-400">{r.new_date}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{r.amount != null ? `${Number(r.amount).toFixed(2)}` : ''}</td>
                          <td className="px-3 py-2 text-center">
                            {r.blocked_by_finalized ? (
                              <Badge variant="outline" className="text-amber-600 border-amber-400 text-xs">blocked</Badge>
                            ) : (
                              <Badge variant="outline" className="text-green-600 border-green-400 text-xs">ready</Badge>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Already correct */}
            {alreadyCorrect.length > 0 && (
              <div>
                <h4 className="text-sm font-medium mb-1 text-muted-foreground">
                  Already at correct date ({alreadyCorrect.length})
                </h4>
                <p className="text-xs text-muted-foreground">
                  {alreadyCorrect.map((r) => r.uplisting_id).join(', ')}
                </p>
              </div>
            )}

            {/* Cancelled drafts */}
            {preview.cancelled_drafts_not_redated?.length > 0 && (
              <div>
                <h4 className="text-sm font-medium mb-2">
                  Cancelled-booking drafts, not re-dated ({preview.cancelled_drafts_count})
                </h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-muted/30">
                        <th className="text-left px-3 py-2 font-medium">Uplisting ID</th>
                        <th className="text-left px-3 py-2 font-medium">Guest</th>
                        <th className="text-left px-3 py-2 font-medium">Current Date</th>
                        <th className="text-right px-3 py-2 font-medium">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.cancelled_drafts_not_redated.map((r) => (
                        <tr key={r.invoice_id} className="border-b bg-red-50/50 dark:bg-red-950/10">
                          <td className="px-3 py-2 font-mono text-xs">{r.uplisting_id}</td>
                          <td className="px-3 py-2 text-xs">{r.guest_name}</td>
                          <td className="px-3 py-2 font-mono text-xs">{r.current_date}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{r.amount != null ? `${Number(r.amount).toFixed(2)}` : ''}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Missing check-in */}
            {preview.missing_checkin_skipped?.length > 0 && (
              <div>
                <h4 className="text-sm font-medium mb-2">
                  Skipped, no check-in ({preview.missing_checkin_count})
                </h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-muted/30">
                        <th className="text-left px-3 py-2 font-medium">Uplisting ID</th>
                        <th className="text-left px-3 py-2 font-medium">Invoice ID</th>
                        <th className="text-left px-3 py-2 font-medium">Raw check_in</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.missing_checkin_skipped.map((r) => (
                        <tr key={r.invoice_id} className="border-b">
                          <td className="px-3 py-2 font-mono text-xs">{r.uplisting_id}</td>
                          <td className="px-3 py-2 font-mono text-xs">{r.invoice_id}</td>
                          <td className="px-3 py-2 font-mono text-xs text-amber-600">{r.check_in_raw || '(empty)'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Finalized invoices per entity */}
            {Object.keys(preview.finalized_per_entity || {}).length > 0 && (
              <div>
                <h4 className="text-sm font-medium mb-2">
                  Finalized invoices per entity (for finalize-order reference)
                </h4>
                {Object.entries(preview.finalized_per_entity).map(([entityId, invoices]) => (
                  <div key={entityId} className="mb-3">
                    <p className="text-xs font-medium text-muted-foreground mb-1">{entityId}</p>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b bg-muted/30">
                            <th className="text-left px-3 py-2 font-medium">Invoice #</th>
                            <th className="text-left px-3 py-2 font-medium">Date</th>
                            <th className="text-right px-3 py-2 font-medium">Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {invoices.map((inv) => (
                            <tr key={inv.invoice_id} className="border-b">
                              <td className="px-3 py-2 font-mono text-xs">{inv.invoice_number || inv.invoice_id}</td>
                              <td className="px-3 py-2 font-mono text-xs">{inv.date}</td>
                              <td className="px-3 py-2 text-right tabular-nums">{inv.amount != null ? `${Number(inv.amount).toFixed(2)}` : ''}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Nothing to do */}
            {preview.date_changes === 0 && preview.cancelled_drafts_count === 0 && preview.missing_checkin_count === 0 && (
              <div className="rounded-lg border p-4 text-center">
                <CheckCircle2 className="h-6 w-6 text-green-500 mx-auto mb-1" />
                <p className="text-sm text-muted-foreground">
                  All drafts are already at their check-in date. Nothing to re-date.
                </p>
              </div>
            )}
          </div>
        )}

        {/* ── Apply result ── */}
        {applyResult && (
          <div className="space-y-3">
            <div className={`rounded-lg border p-3 ${
              applyResult.failed > 0
                ? 'border-amber-300 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/30'
                : 'border-green-300 bg-green-50 dark:border-green-800 dark:bg-green-950/30'
            }`}>
              <div className="flex items-center gap-2">
                {applyResult.failed > 0
                  ? <AlertTriangle className="h-4 w-4 text-amber-600" />
                  : <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />}
                <span className="text-sm font-medium">
                  Re-date complete: {applyResult.applied} updated
                  {applyResult.failed > 0 && `, ${applyResult.failed} failed`}
                  {applyResult.skipped_already_correct > 0 && `, ${applyResult.skipped_already_correct} skipped`}
                </span>
              </div>
            </div>

            {/* Per-invoice results */}
            {applyResult.results?.length > 0 && (
              <div className="overflow-x-auto max-h-96 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0">
                    <tr className="border-b bg-muted/30">
                      <th className="text-left px-3 py-2 font-medium">Uplisting ID</th>
                      <th className="text-left px-3 py-2 font-medium">Action</th>
                      <th className="text-left px-3 py-2 font-medium">Verified Date</th>
                      <th className="text-left px-3 py-2 font-medium">Error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {applyResult.results.map((r, i) => (
                      <tr
                        key={r.invoice_id || i}
                        className={`border-b ${
                          r.action === 'failed' ? 'bg-red-50 dark:bg-red-950/20' :
                          r.action === 'updated' ? '' :
                          'bg-muted/20'
                        }`}
                      >
                        <td className="px-3 py-2 font-mono text-xs">{r.uplisting_id || r.invoice_id}</td>
                        <td className="px-3 py-2">
                          {r.action === 'updated' && (
                            <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 text-xs">updated</Badge>
                          )}
                          {r.action === 'failed' && (
                            <Badge variant="destructive" className="text-xs">failed</Badge>
                          )}
                          {r.action === 'skipped_already_correct' && (
                            <Badge variant="outline" className="text-xs">skipped</Badge>
                          )}
                          {r.action === 'not_in_preview' && (
                            <Badge variant="outline" className="text-amber-600 border-amber-400 text-xs">stale</Badge>
                          )}
                        </td>
                        <td className="px-3 py-2 font-mono text-xs">
                          {r.verified_date || ''}
                          {r.date_matches === false && (
                            <span className="text-red-500 ml-1">(mismatch)</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-xs text-red-600 dark:text-red-400 max-w-xs truncate" title={
                          r.pennylane_error ? JSON.stringify(r.pennylane_error) : r.error || ''
                        }>
                          {r.pennylane_error
                            ? (typeof r.pennylane_error === 'object'
                                ? JSON.stringify(r.pennylane_error)
                                : r.pennylane_error)
                            : r.error || ''}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Stale IDs */}
            {applyResult.stale_ids?.length > 0 && (
              <div className="rounded-lg border border-amber-300 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/30 p-3">
                <p className="text-sm font-medium flex items-center gap-1.5">
                  <AlertTriangle className="h-4 w-4 text-amber-500" />
                  {applyResult.stale_ids.length} invoice{applyResult.stale_ids.length > 1 ? 's' : ''} from
                  your preview are no longer eligible
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  These may have been finalized, cancelled, or deleted since you ran
                  the preview: {applyResult.stale_ids.join(', ')}
                </p>
              </div>
            )}
          </div>
        )}

        {/* ── Confirm dialog ── */}
        <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Confirm draft re-dating</DialogTitle>
              <DialogDescription>
                Re-date {dateChanges.length} draft{dateChanges.length !== 1 ? 's' : ''} to
                their check-in date. This edits live draft invoices in Pennylane.
              </DialogDescription>
            </DialogHeader>
            <div className="text-sm space-y-2 py-2">
              <p>
                Each draft&apos;s <strong>date</strong> and <strong>deadline</strong> will
                be set to the booking&apos;s check-in date.
              </p>
              {preview?.blocked_after_redate_count > 0 && (
                <p className="text-amber-600 dark:text-amber-400">
                  {preview.blocked_after_redate_count} of these will still be blocked from
                  finalization due to finalize-order constraints.
                </p>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setConfirmOpen(false)}>
                Cancel
              </Button>
              <Button onClick={runApply}>
                Re-date {dateChanges.length} draft{dateChanges.length !== 1 ? 's' : ''}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}
